import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { AttachmentPicker } from '@/components/ui/AttachmentPicker'
import { useForm } from '@inertiajs/react'
import { FormEvent, useState, lazy, Suspense } from 'react'
import { Calendar, Eye, EyeOff, MessageCircle, Paperclip } from 'lucide-react'
import type { CommentAttachment } from '@/types'

// Lazy load the ArticleEditor to avoid SSR issues
const ArticleEditor = lazy(() => import('@/components/admin/ArticleEditor'))

interface Community {
  id: number
  name: string
}

interface Category {
  id: number
  name: string
  community_id: number
  parent_id: number | null
}

interface ArticleData {
  id?: number
  slug?: string
  title: string
  excerpt: string
  body: string
  body_json?: string | null
  category_id: number | string
  community_id: number | string
  parent_category_id?: number | null
  published_at: string | null
  comments_enabled?: boolean
  attachments?: CommentAttachment[]
}

interface ArticleFormProps {
  article?: ArticleData
  communities: Community[]
  categories: Category[]
  onSubmit: string
  isEdit?: boolean
}

export function ArticleForm({
  article,
  communities,
  categories,
  onSubmit,
  isEdit = false
}: ArticleFormProps) {
  const [selectedCommunity, setSelectedCommunity] = useState<string>(
    article?.community_id?.toString() || ''
  )

  // Determine initial parent category: if article has a category with parent_id, use that, else use category_id if it's a root
  const getInitialParentCategory = () => {
    if (!article?.category_id) return ''
    const currentCategory = categories.find(c => c.id === Number(article.category_id))
    if (!currentCategory) return ''
    // If current category has a parent, that parent is the selected parent category
    if (currentCategory.parent_id) return currentCategory.parent_id.toString()
    // If current category is a root, it's the parent category
    return currentCategory.id.toString()
  }

  const getInitialSubcategory = () => {
    if (!article?.category_id) return ''
    const currentCategory = categories.find(c => c.id === Number(article.category_id))
    if (!currentCategory) return ''
    // If current category has a parent, it's a subcategory
    if (currentCategory.parent_id) return currentCategory.id.toString()
    // Otherwise no subcategory selected
    return ''
  }

  const [selectedParentCategory, setSelectedParentCategory] = useState<string>(getInitialParentCategory())

  const { data, setData, post, put, processing, errors, transform } = useForm<{
    _method?: string
    title: string
    excerpt: string
    body: string
    body_json: string
    category_id: string
    community_id: string
    published_at: string
    comments_enabled: boolean
    attachments: File[]
    delete_attachment_ids: number[]
  }>({
    _method: isEdit ? 'put' : undefined,
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    body: article?.body || '',
    body_json: article?.body_json || '',
    category_id: article?.category_id?.toString() || '',
    community_id: article?.community_id?.toString() || '',
    published_at: article?.published_at ? article.published_at.slice(0, 16) : '',
    comments_enabled: article?.comments_enabled ?? false,
    attachments: [],
    delete_attachment_ids: []
  })

  transform(d => {
    const payload: Record<string, unknown> = {
      article: {
        title: d.title,
        excerpt: d.excerpt,
        body: d.body,
        body_json: d.body_json,
        category_id: d.category_id,
        community_id: d.community_id,
        published_at: d.published_at,
        comments_enabled: d.comments_enabled,
        attachments: d.attachments,
        delete_attachment_ids: d.delete_attachment_ids
      }
    }
    if (d._method) payload._method = d._method
    return payload
  })

  // Initialize subcategory selection
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>(getInitialSubcategory())

  // Root categories (parent_id is null) filtered by community
  const rootCategories = selectedCommunity
    ? categories.filter(c => c.community_id === parseInt(selectedCommunity) && c.parent_id === null)
    : []

  // Subcategories of the selected parent category
  const subcategories = selectedParentCategory
    ? categories.filter(c => c.parent_id === parseInt(selectedParentCategory))
    : []

  const handleCommunityChange = (communityId: string) => {
    setSelectedCommunity(communityId)
    setSelectedParentCategory('')
    setSelectedSubcategory('')
    setData(prev => ({
      ...prev,
      community_id: communityId,
      category_id: ''
    }))
  }

  const handleParentCategoryChange = (categoryId: string) => {
    setSelectedParentCategory(categoryId)
    setSelectedSubcategory('')
    // Set category_id to parent category (will be overridden if subcategory is selected)
    setData('category_id', categoryId)
  }

  const handleSubcategoryChange = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId)
    // If a subcategory is selected, use it; otherwise use parent category
    setData('category_id', subcategoryId || selectedParentCategory)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit && data.attachments.length === 0) {
      put(onSubmit)
    } else {
      post(onSubmit, { forceFormData: data.attachments.length > 0 })
    }
  }

  const handlePublishNow = () => {
    setData('published_at', new Date().toISOString().slice(0, 16))
  }

  const handleUnpublish = () => {
    setData('published_at', '')
  }

  const isPublished = data.published_at && new Date(data.published_at) <= new Date()
  const isScheduled = data.published_at && new Date(data.published_at) > new Date()

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Main Content */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4">Contenu</h3>
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-1">
                Titre *
              </label>
              <Input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                placeholder="Ex: Les dernières avancées en cardiologie..."
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-text-dark mb-1">
                Extrait
              </label>
              <Textarea
                id="excerpt"
                value={data.excerpt}
                onChange={(e) => setData('excerpt', e.target.value)}
                placeholder="Un court resume de l'article (affiche dans les listes)..."
                rows={2}
              />
              {errors.excerpt && (
                <p className="mt-1 text-sm text-red-600">{errors.excerpt}</p>
              )}
              <p className="mt-1 text-xs text-text-gray">
                Ce texte apparait dans la liste des articles et les aperus.
              </p>
            </div>

            {/* Body - Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-text-dark mb-1">
                Contenu *
              </label>
              <Suspense fallback={
                <div className="h-[400px] border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                  <p className="text-gray-500">Chargement de l'éditeur...</p>
                </div>
              }>
                <ArticleEditor
                  initialContent={data.body_json || undefined}
                  onChange={(content) => {
                    setData('body_json', content)
                    // Also update body with a plain text version for search/SEO
                    try {
                      const blocks = JSON.parse(content)
                      const plainText = blocks.map((block: { content?: Array<{ text?: string }> }) => {
                        if (block.content && Array.isArray(block.content)) {
                          return block.content.map((item: { text?: string }) => item.text || '').join('')
                        }
                        return ''
                      }).join('\n')
                      setData('body', plainText)
                    } catch {
                      // If parsing fails, just use empty string
                    }
                  }}
                />
              </Suspense>
              {errors.body && (
                <p className="mt-1 text-sm text-red-600">{errors.body}</p>
              )}
              {errors.body_json && (
                <p className="mt-1 text-sm text-red-600">{errors.body_json}</p>
              )}
              <p className="mt-1 text-xs text-text-gray">
                Utilisez "/" pour insérer des blocs (titres, images, vidéos, colonnes...).
              </p>
            </div>
          </div>
        </Card>

        {/* Classification */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4">Classification</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Community */}
            <div>
              <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
                Communaute *
              </label>
              <Select
                id="community_id"
                value={data.community_id}
                onChange={(e) => handleCommunityChange(e.target.value)}
                required
              >
                <option value="">Selectionner une communaute</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </Select>
              {errors.community_id && (
                <p className="mt-1 text-sm text-red-600">{errors.community_id}</p>
              )}
            </div>

            {/* Parent Category */}
            <div>
              <label htmlFor="parent_category_id" className="block text-sm font-medium text-text-dark mb-1">
                Categorie *
              </label>
              <Select
                id="parent_category_id"
                value={selectedParentCategory}
                onChange={(e) => handleParentCategoryChange(e.target.value)}
                required
                disabled={!selectedCommunity}
              >
                <option value="">
                  {selectedCommunity ? 'Selectionner une categorie' : 'Choisissez d\'abord une communaute'}
                </option>
                {rootCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              {errors.category_id && (
                <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
              )}
            </div>

            {/* Subcategory */}
            <div>
              <label htmlFor="subcategory_id" className="block text-sm font-medium text-text-dark mb-1">
                Sous-categorie
              </label>
              <Select
                id="subcategory_id"
                value={selectedSubcategory}
                onChange={(e) => handleSubcategoryChange(e.target.value)}
                disabled={!selectedParentCategory || subcategories.length === 0}
              >
                <option value="">
                  {!selectedParentCategory
                    ? 'Choisissez d\'abord une categorie'
                    : subcategories.length === 0
                    ? 'Aucune sous-categorie'
                    : 'Selectionner une sous-categorie'}
                </option>
                {subcategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-text-gray">
                Optionnel si des sous-categories existent
              </p>
            </div>
          </div>
        </Card>

        {/* Publication */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4">Publication</h3>

          <div className="space-y-4">
            {/* Status indicator */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50">
              {!data.published_at && (
                <>
                  <EyeOff size={20} className="text-gray-500" />
                  <div>
                    <p className="font-medium text-text-dark">Brouillon</p>
                    <p className="text-sm text-text-gray">Cet article n'est pas visible publiquement</p>
                  </div>
                </>
              )}
              {isPublished && (
                <>
                  <Eye size={20} className="text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">Publie</p>
                    <p className="text-sm text-text-gray">Cet article est visible publiquement</p>
                  </div>
                </>
              )}
              {isScheduled && (
                <>
                  <Calendar size={20} className="text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-700">Programme</p>
                    <p className="text-sm text-text-gray">
                      Publication prevue le {new Date(data.published_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Date picker */}
            <div>
              <label htmlFor="published_at" className="block text-sm font-medium text-text-dark mb-1">
                Date de publication
              </label>
              <Input
                id="published_at"
                type="datetime-local"
                value={data.published_at}
                onChange={(e) => setData('published_at', e.target.value)}
              />
              <p className="mt-1 text-xs text-text-gray">
                Laissez vide pour enregistrer comme brouillon. Definissez une date future pour programmer la publication.
              </p>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handlePublishNow}
                className="flex items-center gap-2"
              >
                <Eye size={16} />
                Publier maintenant
              </Button>
              {data.published_at && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleUnpublish}
                  className="flex items-center gap-2"
                >
                  <EyeOff size={16} />
                  Depublier
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Attachments */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4 flex items-center gap-2">
            <Paperclip size={18} />
            Pièces jointes
          </h3>
          <AttachmentPicker
            files={data.attachments}
            onFilesChange={(files) => setData('attachments', files)}
            existing={article?.attachments || []}
            deletedIds={data.delete_attachment_ids}
            onDeletedIdsChange={(ids) => setData('delete_attachment_ids', ids)}
            label="Ajouter un fichier"
          />
          {errors.attachments && (
            <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>
          )}
          <p className="mt-2 text-xs text-text-gray">
            Les pièces jointes s'affichent en bas de l'article (JPEG, PNG, GIF, WebP, PDF · 10 Mo max · 5 fichiers).
          </p>
        </Card>

        {/* Comments */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark mb-4">Commentaires</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={data.comments_enabled}
                onChange={(e) => setData('comments_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-tessan-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-tessan-primary" />
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle size={18} className="text-gray-500" />
              <span className="text-sm font-medium text-text-dark">
                Activer les commentaires
              </span>
            </div>
          </label>
          <p className="mt-2 text-xs text-text-gray ml-14">
            Les commentaires sont automatiquement modérés (détection de contenu inapproprié).
          </p>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={() => history.back()}>
            Annuler
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Enregistrement...' : isEdit ? 'Mettre a jour' : 'Creer l\'article'}
          </Button>
        </div>
      </div>
    </form>
  )
}
